/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceSupabase - A library for working with supabase made in Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.
*/

import { isPlatformBrowser } from "@angular/common";
import { inject, Injectable, PLATFORM_ID } from "@angular/core";
import { environment } from "src/environments/environment";
import { LoggerService } from "./logger.service";

type ValidKeys = "authToken" | "isSidebarCollapsed" | "userID" | "tenantId";
const STORAGE_PREFIX = environment.STORAGE_PREFIX;
/**
 * Service for managing cache using local storage.
 */
@Injectable({
	providedIn: "root",
})
export class CacheManagerService {
	private loggerService = inject(LoggerService);
	private platformId = inject(PLATFORM_ID);

	setTenantId(id: string) {
		if (environment.defaultTenant === "") {
			this.set("tenantId", id);
		} else {
			this.set("tenantId", environment.defaultTenant);
		}
	}

	getTenantId() {
		if (environment.defaultTenant === "") {
			const id = this.get<string>("tenantId");
			if (id) {
				this.setTenantId(id);
				return id;
			}
		}
		return environment.defaultTenant;
	}

	/**
	 * Retrieves the value associated with the specified key from the cache.
	 * @param key - The key used to retrieve the value from the cache.
	 * @returns The value associated with the key, or null if the key does not exist in the cache.
	 */
	public get<T>(key: ValidKeys): T | null {
		if (isPlatformBrowser(this.platformId)) {
			const item = localStorage.getItem(STORAGE_PREFIX + key);
			try {
				if (item) {
					return JSON.parse(item);
				}
				return null;
			} catch (error) {
				this.loggerService.logError("Error parsing JSON", error);
				return null;
			}
		} else return null;
	}

	/**
	 * Sets the value associated with the specified key in the cache.
	 * @param key - The key used to store the value in the cache.
	 * @param value - The value to be stored in the cache.
	 * @returns void
	 */
	public set<T>(key: ValidKeys, value: T): void {
		if (isPlatformBrowser(this.platformId)) {
			localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(value));
		}
	}

	/**
	 * Removes the value associated with the specified key from the cache.
	 * @param key - The key used to remove the value from the cache.
	 * @returns void
	 */
	public remove(key: ValidKeys): void {
		if (isPlatformBrowser(this.platformId)) {
			localStorage.removeItem(STORAGE_PREFIX + key);
		}
	}

	/**
	 * Removes all values from the cache.
	 * @returns void
	 * @remarks This method should be used with caution.
	 */
	public removeAll(): void {
		if (isPlatformBrowser(this.platformId)) {
			localStorage.clear();
			sessionStorage.clear();
		}
	}
}
